import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SettlementInLineWithOptions, TimeOptions } from './types';
import SettlementDatePayable from './SettlementDatePayable';
import './SettlementDate.scss';
import { ErrorState } from 'types/Payable';
import {
  FormCondition,
  FormConditionCondition,
  FormConditionFromDateTimeUnit,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import {
  AgxDatePicker,
  AgxDropdown,
  AgxRadioGroup,
  AgxSatisfactionOfSpecialCondition,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  Icon_AlertCircle,
} from '@urbanx/agx.ui-components2';

interface SettlementDateProps {
  id: string;
  label: string;
  onValueChanged: (change: { id: string; value: FormCondition }) => void;
  defaultValue: FormCondition;
  validate?: boolean;
  required?: boolean;
  showSpecialConditionOption?: boolean;
  showAsPerContractOption?: boolean;
  showInLineWithOption?: boolean;
  showFromContractDateDropdownOption?: boolean;
  showPayableOptions?: boolean;
  error?: ErrorState;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const radioOptions = [
  {
    label: 'As per Contract',
    value: FormConditionCondition.AsPerContract,
  },
  {
    label: '__day(s) from Contract Date',
    value: FormConditionCondition.NdaysFromDate,
  },
  {
    label: 'Specific Date',
    value: FormConditionCondition.OnSpecificDate,
  },
  {
    label: 'Refer to Special Condition',
    value: FormConditionCondition.UponSatisfactionOfSpecialCondition,
  },
  {
    label: 'In line with',
    value: FormConditionCondition.Other,
  },
];

const SettlementDateComponent: React.FC<SettlementDateProps> = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  validate = false,
  required = false,
  showSpecialConditionOption,
  showAsPerContractOption,
  showFromContractDateDropdownOption,
  showPayableOptions,
  error,
  specialConditions,
  campaignId,
}: SettlementDateProps) => {
  const [settlementDate, setSettlementDate] = useState<FormCondition>({
    ...defaultValue,
    condition: showAsPerContractOption
      ? (defaultValue?.condition ?? FormConditionCondition.AsPerContract)
      : defaultValue?.condition,
    conditionType: defaultValue?.conditionType,
    daysFromDate: defaultValue?.daysFromDate,
    fromDateTimeUnit:
      defaultValue?.fromDateTimeUnit ?? FormConditionFromDateTimeUnit.Days,
    referenceDate: defaultValue?.referenceDate,
    other: defaultValue?.other,
    specialConditionId: defaultValue?.specialConditionId,
  });

  const { condition, daysFromDate, referenceDate, fromDateTimeUnit, other } =
    settlementDate;

  useEffect(() => {
    onValueChanged({ id, value: settlementDate });
  }, [settlementDate]);

  const onNumberOfDaysChange = useCallback(({ value }: { value: string }) => {
    const number = parseInt(value, 10);

    setSettlementDate(prev => ({
      ...prev,
      daysFromDate: Number.isNaN(number) ? undefined : number,
    }));
  }, []);

  const onDaysTypeChange = useCallback((value: string) => {
    const dayType = value as FormConditionFromDateTimeUnit;

    setSettlementDate(prev => ({
      ...prev,
      fromDateTimeUnit: dayType,
    }));
  }, []);

  const onSpecialConditionChange = useCallback(
    (specialConditionId?: string) => {
      setSettlementDate(prev => ({
        ...prev,
        specialConditionId: specialConditionId,
      }));
    },
    []
  );

  const onCheckChanged = useCallback((val: string) => {
    const value = val as FormConditionCondition;

    setSettlementDate(prev => ({
      ...prev,
      condition: value,
    }));
  }, []);

  const onInLineWithOptionChange = useCallback((value: string) => {
    setSettlementDate(prev => ({
      ...prev,
      other: value,
    }));
  }, []);

  const filteredRadioOptions = useMemo(() => {
    return radioOptions.filter(option => {
      if (option.value === FormConditionCondition.AsPerContract) {
        return showAsPerContractOption;
      }

      if (
        option.value ===
        FormConditionCondition.UponSatisfactionOfSpecialCondition
      ) {
        return showSpecialConditionOption;
      }
      return true;
    });
  }, [showAsPerContractOption]);

  if (showPayableOptions) {
    return (
      <>
        <AgxTypography
          id={`settlementDateLabel-${id}`}
          type="label"
          size="medium"
          color="var(--text-primary)"
        >
          {label}
        </AgxTypography>
        <SettlementDatePayable
          id={id}
          onValueChanged={({ value: settlementDateConditionValue }) =>
            setSettlementDate({
              ...settlementDate,
              ...settlementDateConditionValue,
            })
          }
          defaultValue={settlementDate}
          validate={validate}
          error={error}
          required={true}
          specialConditions={specialConditions}
          campaignId={campaignId}
        />
      </>
    );
  }

  return (
    <>
      <AgxRadioGroup
        id={`settlementDate-radio-group-${id}`}
        label={label}
        layout={'vertical'}
        options={filteredRadioOptions}
        value={condition}
        onChange={onCheckChanged}
        subContent={
          <>
            {condition === FormConditionCondition.NdaysFromDate && (
              <>
                {showFromContractDateDropdownOption && (
                  <>
                    <AgxSpacer
                      direction="horizontal"
                      gap="xs"
                      padding="md"
                      contentAlignment="baseline"
                    >
                      <AgxTextInput
                        id="numberOfDays"
                        type="number"
                        noHeader
                        maxLength={3}
                        defaultValue={daysFromDate?.toString() || ''}
                        onInputValueChange={onNumberOfDaysChange}
                        size="medium"
                        textAlign="left"
                      />
                      <AgxDropdown
                        options={TimeOptions}
                        id={`timeUnit-${id}`}
                        selected={fromDateTimeUnit}
                        onChange={onDaysTypeChange}
                        noHeader
                        required
                        minWidth="140px"
                      />
                      <AgxTypography
                        id={`fromContractDateLabel`}
                        type="body_ui"
                        size="medium"
                        color="var(--text-primary)"
                      >
                        from Contract date
                      </AgxTypography>
                    </AgxSpacer>
                    {validate && required && !settlementDate.daysFromDate && (
                      <div className={'errorMessage'}>
                        <Icon_AlertCircle type={'danger'} />
                        <AgxTypography
                          id="depositPayable-error"
                          size="small"
                          type="body_ui"
                          color="var(--text-error)"
                        >
                          {`Enter ${fromDateTimeUnit?.toString()}`}
                        </AgxTypography>
                      </div>
                    )}
                  </>
                )}
                {!showFromContractDateDropdownOption && (
                  <AgxSpacer
                    direction="horizontal"
                    gap="xs"
                    fill
                    padding="md"
                    contentAlignment="baseline"
                  >
                    <AgxTextInput
                      id="numberOfDays"
                      type="number"
                      noHeader
                      maxLength={3}
                      defaultValue={daysFromDate?.toString() || ''}
                      onInputValueChange={onNumberOfDaysChange}
                      size="medium"
                      textAlign="left"
                      errorText={
                        validate && !settlementDate.daysFromDate
                          ? 'Enter days'
                          : ''
                      }
                    />
                    <AgxTypography
                      id={`fromContractDateLabel`}
                      type="body_ui"
                      size="medium"
                      color="var(--text-primary)"
                    >
                      days from Contract date
                    </AgxTypography>
                  </AgxSpacer>
                )}
              </>
            )}
            {condition === FormConditionCondition.OnSpecificDate && (
              <AgxSpacer
                direction="horizontal"
                gap="xs"
                fill
                padding="md"
                contentAlignment="baseline"
              >
                <AgxDatePicker
                  key="settlementDateOnSpecificDate"
                  id="settlementDateOnSpecificDate"
                  onChange={value =>
                    setSettlementDate({
                      ...settlementDate,
                      referenceDate: value ?? undefined,
                    })
                  }
                  required
                  noHeader
                  value={referenceDate}
                  stretch
                />
              </AgxSpacer>
            )}
            {condition ===
              FormConditionCondition.UponSatisfactionOfSpecialCondition && (
                <AgxSpacer
                  direction="horizontal"
                  gap="xs"
                  fill
                  padding="md"
                  contentAlignment="baseline"
                >
                  <AgxSatisfactionOfSpecialCondition
                    id={
                      'settlementDateOnSpecialClause-satisfactionOfSpecialCondition'
                    }
                    key="settlementDateOnSpecialClause-satisfactionOfSpecialCondition"
                    specialConditions={specialConditions}
                    onChange={onSpecialConditionChange}
                    campaignId={campaignId}
                    selectedClauseId={settlementDate?.specialConditionId}
                  />
                </AgxSpacer>
              )}
            {condition === FormConditionCondition.Other && (
              <AgxSpacer
                direction="horizontal"
                gap="xs"
                fill
                padding="md"
                contentAlignment="baseline"
              >
                <AgxRadioGroup
                  id={`settlementDate-inlineWith-radio-group-${id}`}
                  layout={'vertical'}
                  options={SettlementInLineWithOptions}
                  value={other}
                  onChange={onInLineWithOptionChange}
                />
              </AgxSpacer>
            )}
          </>
        }
      />
      {validate && required && !settlementDate.condition && (
        <div className={'errorMessage'}>
          <Icon_AlertCircle type={'danger'} />
          <AgxTypography
            id="depositPayable-error"
            size="small"
            type="body_ui"
            color="var(--text-error)"
          >
            {'Select an option'}
          </AgxTypography>
        </div>
      )}
    </>
  );
};

export default SettlementDateComponent;
